import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { MapPin, CalendarDays, Hotel, BusFront, UtensilsCrossed, GitCompareArrows, Check, ArrowRight } from "lucide-react";
import { inr, getCompare, toggleComparePkg } from "@/lib/api";

const AVAIL_STYLES = {
    enquiry: "bg-gold/20 text-yellow-800",
    available: "bg-green-100 text-green-800",
    limited: "bg-orange-100 text-orange-700",
    request: "bg-stone-100 text-stone-600",
};

function availability(pkg) {
    if (pkg.enquiry_only) return { key: "enquiry", label: "Enquiry only" };
    const live = (pkg.departures || []).find((d) => d.status === "available");
    if (live) return { key: "available", label: `Next: ${new Date(live.date).toLocaleDateString("en-IN", { day: "numeric", month: "short" })}` };
    if ((pkg.departures || []).some((d) => d.status === "limited")) return { key: "limited", label: "Filling fast" };
    return { key: "request", label: "Dates on request" };
}

export function PackageCard({ pkg }) {
    const [inCompare, setInCompare] = useState(() => getCompare().some((s) => s.slug === pkg.slug));
    useEffect(() => {
        const h = () => setInCompare(getCompare().some((s) => s.slug === pkg.slug));
        window.addEventListener("gc-compare-changed", h);
        return () => window.removeEventListener("gc-compare-changed", h);
    }, [pkg.slug]);

    const avail = availability(pkg);
    const price = pkg.starting_price;

    return (
        <div className="bg-white rounded-2xl border border-stone-300 overflow-hidden hover:border-saffron hover:-translate-y-0.5 transition-all flex flex-col" data-testid={`package-card-${pkg.slug}`}>
            <div className="relative h-44 overflow-hidden">
                <img src={pkg.cover_image} alt={`${pkg.name} — ${pkg.destination_name}`} loading="lazy" className="h-full w-full object-cover" />
                <div className="absolute top-3 left-3 flex gap-2">
                    {(pkg.badges || []).map((b) => (
                        <span key={b} className="rounded-full bg-navy text-white text-[10px] font-black uppercase tracking-wider px-2.5 py-1" data-testid={`badge-${pkg.slug}`}>{b}</span>
                    ))}
                </div>
                <span className={`absolute top-3 right-3 rounded-full text-[10px] font-black uppercase tracking-wider px-2.5 py-1 ${AVAIL_STYLES[avail.key]}`} data-testid={`availability-${pkg.slug}`}>{avail.label}</span>
            </div>

            <div className="p-5 flex flex-col flex-1">
                <div className="flex items-start justify-between gap-3">
                    <div>
                        <h3 className="font-display font-black text-lg text-navy leading-tight">{pkg.name}</h3>
                        <p className="mt-0.5 text-xs font-bold text-stone-500 flex items-center gap-1"><MapPin className="h-3 w-3" />{pkg.destination_name}</p>
                    </div>
                    <span className="shrink-0 rounded-full bg-saffron/10 text-saffron text-[11px] font-black px-2.5 py-1 flex items-center gap-1">
                        <CalendarDays className="h-3 w-3" />{pkg.days}D/{pkg.nights}N
                    </span>
                </div>

                <div className="mt-3 flex flex-wrap gap-x-4 gap-y-1 text-[11px] font-bold text-stone-500">
                    {pkg.departure_cities?.length > 0 && <span>Ex: {pkg.departure_cities.join(", ")}</span>}
                    {pkg.hotel_category && <span className="flex items-center gap-1"><Hotel className="h-3 w-3" />{pkg.hotel_category}</span>}
                    {pkg.transport_type && <span className="flex items-center gap-1"><BusFront className="h-3 w-3" />{pkg.transport_type}</span>}
                    {pkg.meal_plan && <span className="flex items-center gap-1"><UtensilsCrossed className="h-3 w-3" />{pkg.meal_plan}</span>}
                </div>

                {(pkg.highlights || []).length > 0 && (
                    <p className="mt-2.5 text-xs text-stone-600 leading-relaxed">{pkg.highlights.slice(0, 3).join(" · ")}</p>
                )}

                <div className="mt-4 pt-4 border-t border-stone-200 flex items-end justify-between gap-2">
                    <div>
                        {price != null ? (
                            <>
                                <p className="font-display font-black text-xl text-navy" data-testid={`price-${pkg.slug}`}>{inr(price)}<span className="text-xs font-bold text-stone-500"> /person</span></p>
                                {pkg.price_estimated && <p className="text-[10px] font-bold text-stone-400 uppercase tracking-wider">Estimated — reconfirm on enquiry</p>}
                            </>
                        ) : (
                            <p className="font-display font-black text-base text-navy" data-testid={`price-${pkg.slug}`}>Price on enquiry</p>
                        )}
                    </div>
                    <label className={`shrink-0 inline-flex items-center gap-1.5 text-[11px] font-black uppercase tracking-wider cursor-pointer rounded-full border px-3 py-1.5 transition-colors ${inCompare ? "bg-navy text-white border-navy" : "border-stone-300 text-stone-500 hover:border-navy hover:text-navy"}`} data-testid={`compare-toggle-${pkg.slug}`}>
                        <input type="checkbox" className="hidden" checked={inCompare} onChange={() => toggleComparePkg(pkg)} />
                        {inCompare ? <Check className="h-3 w-3" /> : <GitCompareArrows className="h-3 w-3" />} Compare
                    </label>
                </div>

                <div className="mt-4 grid grid-cols-3 gap-2">
                    <Link to={`/package/${pkg.slug}`} data-testid={`view-itinerary-${pkg.slug}`} className="rounded-full border border-navy text-navy text-[10px] font-display font-black uppercase tracking-wider py-2 text-center hover:bg-navy hover:text-white transition-colors">Itinerary</Link>
                    <Link to={`/package/${pkg.slug}#price`} data-testid={`view-price-${pkg.slug}`} className="rounded-full border border-stone-300 text-stone-600 text-[10px] font-display font-black uppercase tracking-wider py-2 text-center hover:border-saffron hover:text-saffron transition-colors">Price</Link>
                    <Link to={`/package/${pkg.slug}?enquire=1`} data-testid={`enquire-${pkg.slug}`} className="rounded-full bg-saffron text-white text-[10px] font-display font-black uppercase tracking-wider py-2 text-center hover:bg-saffron-dark transition-colors inline-flex items-center justify-center gap-1">Enquire <ArrowRight className="h-3 w-3" /></Link>
                </div>
            </div>
        </div>
    );
}
